import {
  AutodialerBucketConfiguration,
  AutodialerBucketConfigurationRequest,
  BookerLeague,
  BookingConfiguration,
  BookingReservation,
  CallableCasesAnalyticsRow,
  NoeticaAutodialAdminConfig,
  QualifyingAnswerExport,
  SlotConfiguration,
} from '../dto/admin-config-models';
import { CustomerRule } from '../dto/admin-rule-models';
import { FlowQuestion, FlowQuestionRule, QualificationFlow, QualifyingFlowOption, QualifyingFlowQuestion } from '../dto/qualifying-flow-models';
import { CaseInfo, GroupSlotData, Result, SalesChannel } from '../dto/model';

const rootUrl = (process as any).env.REACT_APP_API_ROOT_URL;

export async function getConfigurations(accessToken: string, salesChannel: SalesChannel): Promise<BookingConfiguration[] | null> {
  try {
    const response = await fetch(`${rootUrl}/api/v1/admin/configurations?salesChannel=${salesChannel}`, {
      method: 'GET',
      headers: new Headers({
        Authorization: `Bearer ${accessToken}`,
      }),
    });
    const data = await response.json();
    return data;
  } catch (error) {
    alert(`Failed to get configuration. Exception while loading ${error}`);
  }
  return null;
}

export async function postConfigurations(accessToken: string, payload: BookingConfiguration[]): Promise<Result<number>> {
  try {
    const response = await fetch(`${rootUrl}/api/v1/admin/configurations`, {
      method: 'POST',
      headers: new Headers({
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify(payload),
    });
    return await response.json();
  } catch (error) {
    alert(error);
    return {
      data: 0,
      errors: ['Failed to save configuration'],
      isSuccess: false,
    };
  }
}

export async function getCustomerRules(accessToken: string): Promise<Result<CustomerRule[] | null>> {
  try {
    const url = `${rootUrl}/api/v1/opportunity/rules`;
    const response = await fetch(url, {
      method: 'GET',
      headers: new Headers({
        Authorization: `Bearer ${accessToken}`,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      return {
        data: null,
        errors: [errorText || 'Failed to load customer rules.'],
        isSuccess: false,
      };
    }

    const data = (await response.json()) as Result<CustomerRule[]>;

    if (data.isSuccess) {
      data.data?.map((x) => (x.feId = crypto.randomUUID()));
      data.data?.map((x) => x.conditions.map((y) => (y.feId = crypto.randomUUID())));
      data.data?.map((x) => x.outcomes.map((y) => (y.feId = crypto.randomUUID())));
    }

    return data;
  } catch (error) {
    alert(`Failed to get configuration. Exception while loading ${error}`);
    return {
      data: null,
      errors: [`${JSON.stringify(error)}`],
      isSuccess: false,
    };
  }
}

export async function postCustomerRules(accessToken: string, payload: CustomerRule[]): Promise<Result<number>> {
  try {
    const url = `${rootUrl}/api/v1/opportunity/rules`;
    const response = await fetch(url, {
      method: 'POST',
      headers: new Headers({
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify(payload),
    });
    const data = await response.json();

    return data;
  } catch (error) {
    alert(`Failed to get configuration. Exception while loading ${error}`);
    return {
      data: -1,
      errors: [`${JSON.stringify(error)}`],
      isSuccess: false,
    };
  }
}

export async function getOppsMatchingConditions(accessToken: string, payload: CustomerRule): Promise<Result<CaseInfo[]>> {
  try {
    const url = `${rootUrl}/api/v1/opportunity/rules/match`;
    const response = await fetch(url, {
      method: 'POST',
      headers: new Headers({
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify(payload),
    });
    const data = await response.json();

    return data;
  } catch (error) {
    alert(`Failed to get configuration. Exception while loading ${error}`);
    return {
      data: [],
      errors: [`${JSON.stringify(error)}`],
      isSuccess: false,
    };
  }
}

export async function getGroupSlotData(accessToken: string, startDate: string, endDate: string): Promise<Result<GroupSlotData[]>> {
  try {
    const response = await fetch(`${rootUrl}/api/v1/analytics/slot?startDate=${startDate}&endDate=${endDate}`, {
      method: 'GET',
      headers: new Headers({
        Authorization: `Bearer ${accessToken}`,
      }),
    });
    const data = await response.json();
    return data as Result<GroupSlotData[]>;
  } catch (error) {
    alert(`Failed to get configuration. Exception while loading ${error}`);
    return {
      data: [],
      errors: [`${JSON.stringify(error)}`],
      isSuccess: false,
    };
  }
}

export async function getBookingReservationsData(accessToken: string, startDate: string, endDate: string): Promise<Result<BookingReservation[]>> {
  try {
    const response = await fetch(`${rootUrl}/api/v1/analytics/booking-reservation?startDate=${startDate}&endDate=${endDate}`, {
      method: 'GET',
      headers: new Headers({
        Authorization: `Bearer ${accessToken}`,
      }),
    });
    const data = await response.json();

    return data as Result<BookingReservation[]>;
  } catch (error) {
    alert(`Failed to get booking reservations. Exception while loading ${error}`);
    return {
      data: [],
      errors: [`${JSON.stringify(error)}`],
      isSuccess: false,
    };
  }
}

export async function getBookerLeagues(accessToken: string): Promise<Result<BookerLeague[]> | null> {
  try {
    const response = await fetch(`${rootUrl}/api/v1/admin/bookerleague`, {
      method: 'GET',
      headers: new Headers({
        Authorization: `Bearer ${accessToken}`,
      }),
    });
    const data = await response.json();
    return data;
  } catch (error) {
    alert(`Failed to get booker leagues. Exception while loading ${error}`);
  }
  return null;
}

export async function postBookerLeagues(accessToken: string, payload: BookerLeague[]): Promise<Result<number>> {
  try {
    const response = await fetch(`${rootUrl}/api/v1/admin/bookerleague`, {
      method: 'POST',
      headers: new Headers({
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify(payload),
    });
    return await response.json();
  } catch (error) {
    alert(error);
    return {
      data: 0,
      errors: ['Failed to save booker leagues'],
      isSuccess: false,
    };
  }
}

export async function getSlotConfigs(accessToken: string): Promise<Result<SlotConfiguration[]> | null> {
  try {
    const response = await fetch(`${rootUrl}/api/v1/admin/slotconfig`, {
      method: 'GET',
      headers: new Headers({
        Authorization: `Bearer ${accessToken}`,
      }),
    });
    const data = (await response.json()) as Result<SlotConfiguration[]>;
    if (data.isSuccess) {
      data.data?.map((x) => (x.feId = crypto.randomUUID()));
    }

    return data;
  } catch (error) {
    alert(`Failed to load slot configurations: ${error}`);
    return null;
  }
}

export async function postAppointmentsLimitConfiguration(accessToken: string, payload: SlotConfiguration[]): Promise<Result<number>> {
  try {
    const response = await fetch(`${rootUrl}/api/v1/admin/slotconfig`, {
      method: 'POST',
      headers: new Headers({
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify(payload),
    });

    return (await response.json()) as Result<number>;
  } catch (error) {
    alert(`Failed to save slot configurations: ${error}`);
    return { data: 0, errors: [String(error)], isSuccess: false };
  }
}

export async function getQualifyingAnswersExport(accessToken: string, startDate: string, endDate: string): Promise<Result<QualifyingAnswerExport[]>> {
  try {
    const response = await fetch(`${rootUrl}/api/v1/qualifying-answers/export?startDate=${startDate}&endDate=${endDate}`, {
      method: 'GET',
      headers: new Headers({
        Authorization: `Bearer ${accessToken}`,
      }),
    });
    const data = await response.json();

    return data as Result<QualifyingAnswerExport[]>;
  } catch (error) {
    alert(`Failed to get qualifying answers export. Exception while loading ${error}`);
    return {
      data: [],
      errors: [`${JSON.stringify(error)}`],
      isSuccess: false,
    };
  }
}

// ─── Qualifying Questions (library) ────────────────────────────────────────

/** Convert FE QualifyingFlowQuestion to the backend QualifyingQuestion wire format.
 *  The backend model stores QuestionText and Options as raw JSON strings. */
function toBackendQuestion(q: QualifyingFlowQuestion) {
  let optionsJson: string | null = null;
  if (q.questionType === 'Dropdown') {
    optionsJson = JSON.stringify(q.options.map((o) => ({ Key: o.key, Text: o.text })));
  } else if (q.questionType === 'NumberField' && q.allowEmpty) {
    optionsJson = JSON.stringify({ AllowEmpty: true });
  }

  return {
    id: q.id,
    questionKey: q.questionKey,
    questionText: JSON.stringify(q.questionText),
    questionType: q.questionType,
    options: optionsJson,
    sortOrder: q.sortOrder,
    isActive: q.isActive,
    createdBy: '',
  };
}

/** Convert backend QualifyingQuestionDto options (object) to FE QualifyingFlowOption[]. */
export function fromBackendOptions(questionType: string, options: any): QualifyingFlowOption[] {
  if (!options) return [];
  if (questionType === 'Dropdown' && Array.isArray(options)) {
    return options.map((o: any, idx: number) => ({
      feId: crypto.randomUUID(),
      key: o.Key ?? o.key ?? `option_${idx}`,
      text: o.Text ?? o.text ?? {},
    }));
  }
  return [];
}

/** Convert backend rules array (camelCase or PascalCase) to FE FlowQuestionRule[]. */
export function fromBackendRules(rules: any): FlowQuestionRule[] {
  if (!rules || !Array.isArray(rules)) return [];
  return rules.map((r: any) => ({
    when: {
      optionKey: r.When?.OptionKey ?? r.when?.optionKey,
      operator: r.When?.Operator ?? r.when?.operator,
      value: r.When?.Value ?? r.when?.value,
    },
    then: {
      route: r.Then?.Route ?? r.then?.route,
      skipNext: r.Then?.SkipNext ?? r.then?.skipNext,
      goTo: r.Then?.GoTo ?? r.then?.goTo,
    },
  }));
}

export async function getQualifyingQuestionsAdmin(accessToken: string): Promise<Result<QualifyingFlowQuestion[]>> {
  try {
    const response = await fetch(`${rootUrl}/api/v1/qualifying-questions?activeOnly=false`, {
      method: 'GET',
      headers: new Headers({
        Authorization: `Bearer ${accessToken}`,
      }),
    });
    const data = (await response.json()) as Result<any[]>;
    if (data.isSuccess) {
      const converted: QualifyingFlowQuestion[] = (data.data ?? []).map((q: any) => ({
        id: q.id,
        feId: crypto.randomUUID(),
        questionKey: q.questionKey,
        questionText: q.questionText ?? {},
        questionType: q.questionType,
        options: fromBackendOptions(q.questionType, q.options),
        sortOrder: q.sortOrder,
        isActive: q.isActive ?? true,
      }));
      return { data: converted, errors: [], isSuccess: true };
    }
    return data as unknown as Result<QualifyingFlowQuestion[]>;
  } catch (error) {
    alert(`Failed to get qualifying questions. Exception while loading ${error}`);
    return { data: [], errors: [`${JSON.stringify(error)}`], isSuccess: false };
  }
}

export async function postQualifyingQuestion(accessToken: string, question: QualifyingFlowQuestion): Promise<Result<void>> {
  try {
    const response = await fetch(`${rootUrl}/api/v1/qualifying-questions`, {
      method: 'POST',
      headers: new Headers({
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify(toBackendQuestion(question)),
    });
    return await response.json();
  } catch (error) {
    alert(`Failed to create qualifying question. ${error}`);
    return { data: undefined, errors: [`${JSON.stringify(error)}`], isSuccess: false };
  }
}

export async function putQualifyingQuestion(accessToken: string, question: QualifyingFlowQuestion): Promise<Result<void>> {
  try {
    const response = await fetch(`${rootUrl}/api/v1/qualifying-questions/${question.id}`, {
      method: 'PUT',
      headers: new Headers({
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify(toBackendQuestion(question)),
    });
    return await response.json();
  } catch (error) {
    alert(`Failed to update qualifying question. ${error}`);
    return { data: undefined, errors: [`${JSON.stringify(error)}`], isSuccess: false };
  }
}

// ─── Qualification Flows ────────────────────────────────────────────────────

export async function getQualificationFlows(accessToken: string): Promise<Result<QualificationFlow[]>> {
  try {
    const response = await fetch(`${rootUrl}/api/v1/qualification-flows`, {
      method: 'GET',
      headers: new Headers({
        Authorization: `Bearer ${accessToken}`,
      }),
    });
    const data = (await response.json()) as Result<any[]>;
    if (data.isSuccess) {
      const flows: QualificationFlow[] = (data.data ?? []).map((f: any) => ({
        id: f.id,
        feId: crypto.randomUUID(),
        name: f.name,
        description: f.description ?? '',
        salesChannel: f.salesChannel,
        isActive: f.isActive ?? false,
        questions: (f.questions ?? []).map((slot: any): FlowQuestion => {
          const q = slot.question ?? slot;
          return {
            feId: crypto.randomUUID(),
            question: {
              id: q.id,
              feId: crypto.randomUUID(),
              questionKey: q.questionKey,
              questionText: q.questionText ?? {},
              questionType: q.questionType,
              options: fromBackendOptions(q.questionType, q.options),
              allowEmpty: q.questionType === 'NumberField' ? (q.options?.AllowEmpty ?? q.options?.allowEmpty ?? false) : undefined,
              sortOrder: q.sortOrder,
              isActive: q.isActive ?? true,
            },
            rules: fromBackendRules(slot.rules ?? []),
          };
        }),
      }));
      return { data: flows, errors: [], isSuccess: true };
    }
    return data as unknown as Result<QualificationFlow[]>;
  } catch (error) {
    alert(`Failed to get qualification flows. Exception while loading ${error}`);
    return { data: [], errors: [`${JSON.stringify(error)}`], isSuccess: false };
  }
}

export async function postQualificationFlow(accessToken: string, flow: QualificationFlow): Promise<Result<void>> {
  try {
    const payload = {
      id: flow.id,
      name: flow.name,
      description: flow.description,
      salesChannel: flow.salesChannel,
      isActive: flow.isActive,
      questions: flow.questions.map((fq) => ({ questionId: fq.question.id, rules: fq.rules })),
    };
    const response = await fetch(`${rootUrl}/api/v1/qualification-flows`, {
      method: 'POST',
      headers: new Headers({
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify(payload),
    });
    return await response.json();
  } catch (error) {
    alert(`Failed to save qualification flow. ${error}`);
    return { data: undefined, errors: [`${JSON.stringify(error)}`], isSuccess: false };
  }
}

export async function deleteQualificationFlow(accessToken: string, id: number): Promise<Result<void>> {
  try {
    const response = await fetch(`${rootUrl}/api/v1/qualification-flows/${id}`, {
      method: 'DELETE',
      headers: new Headers({
        Authorization: `Bearer ${accessToken}`,
      }),
    });
    return await response.json();
  } catch (error) {
    alert(`Failed to delete qualification flow. ${error}`);
    return { data: undefined, errors: [`${JSON.stringify(error)}`], isSuccess: false };
  }
}

function normalizeNoeticaAutodialConfig(raw: Record<string, unknown>): NoeticaAutodialAdminConfig {
  const cli = (raw.cliRotationNumbers ?? raw.CliRotationNumbers) as unknown;
  const blocked = (raw.blockedPhoneNumbers ?? raw.BlockedPhoneNumbers) as unknown;
  const lastMod = (raw.lastModifiedUtc ?? raw.LastModifiedUtc) as string | undefined;
  const modBy = (raw.modifiedBy ?? raw.ModifiedBy) as string | null | undefined;
  return {
    cliRotationNumbers: Array.isArray(cli) ? (cli as string[]) : [],
    blockedPhoneNumbers: Array.isArray(blocked) ? (blocked as string[]) : [],
    lastModifiedUtc: lastMod ?? '',
    modifiedBy: modBy ?? null,
  };
}

export async function getNoeticaAutodialConfig(accessToken: string): Promise<NoeticaAutodialAdminConfig | null> {
  try {
    const response = await fetch(`${rootUrl}/api/v1/admin/noetica-autodial-config`, {
      method: 'GET',
      headers: new Headers({ Authorization: `Bearer ${accessToken}` }),
    });
    if (!response.ok) {
      return null;
    }
    const raw = (await response.json()) as Record<string, unknown>;
    return normalizeNoeticaAutodialConfig(raw);
  } catch {
    return null;
  }
}

export async function putNoeticaAutodialConfig(
  accessToken: string,
  payload: { cliRotationNumbers: string[]; blockedPhoneNumbers: string[] },
): Promise<boolean> {
  try {
    const response = await fetch(`${rootUrl}/api/v1/admin/noetica-autodial-config`, {
      method: 'PUT',
      headers: new Headers({
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify(payload),
    });
    return response.ok;
  } catch {
    return false;
  }
}

export async function getAutodialerBuckets(accessToken: string): Promise<AutodialerBucketConfiguration[] | null> {
  try {
    const response = await fetch(`${rootUrl}/api/v1/admin/autodialer/buckets`, {
      method: 'GET',
      headers: new Headers({ Authorization: `Bearer ${accessToken}` }),
    });
    if (!response.ok) return null;
    return (await response.json()) as AutodialerBucketConfiguration[];
  } catch {
    return null;
  }
}

export async function getPriorityBreakdown(accessToken: string): Promise<CallableCasesAnalyticsRow[] | null> {
  try {
    const response = await fetch(`${rootUrl}/api/v1/analytics/cases?groupBy=priority`, {
      method: 'GET',
      headers: new Headers({ Authorization: `Bearer ${accessToken}` }),
    });
    if (!response.ok) return null;
    return (await response.json()) as CallableCasesAnalyticsRow[];
  } catch {
    return null;
  }
}

export async function createAutodialerBucket(
  accessToken: string,
  request: AutodialerBucketConfigurationRequest,
): Promise<{ id: string } | { errors: string } | null> {
  try {
    const response = await fetch(`${rootUrl}/api/v1/admin/autodialer/buckets`, {
      method: 'POST',
      headers: new Headers({ Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' }),
      body: JSON.stringify(request),
    });
    if (response.status === 400) {
      const body = (await response.json()) as { Errors?: string };
      return { errors: body.Errors ?? 'Validation failed' };
    }
    if (!response.ok) return null;
    const id = (await response.json()) as string;
    return { id };
  } catch {
    return null;
  }
}

export async function updateAutodialerBucket(
  accessToken: string,
  id: string,
  request: AutodialerBucketConfigurationRequest,
): Promise<true | { errors: string } | null> {
  try {
    const response = await fetch(`${rootUrl}/api/v1/admin/autodialer/buckets/${encodeURIComponent(id)}`, {
      method: 'PUT',
      headers: new Headers({ Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' }),
      body: JSON.stringify({ ...request, id }),
    });
    if (response.status === 400) {
      const body = (await response.json()) as { Errors?: string };
      return { errors: body.Errors ?? 'Validation failed' };
    }
    if (!response.ok) return null;
    return true;
  } catch {
    return null;
  }
}

export async function deleteAutodialerBucket(accessToken: string, id: string): Promise<boolean> {
  try {
    const response = await fetch(`${rootUrl}/api/v1/admin/autodialer/buckets/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      headers: new Headers({ Authorization: `Bearer ${accessToken}` }),
    });
    return response.ok;
  } catch {
    return false;
  }
}
